import type { GrocLandingItemsParams, Store, StoreLanding } from "$lib/def/landing";
import type { Address } from "./groc-cart";

import { browser } from "$app/environment";
import { profile$ } from "$lib/serv/core/auth";
import { fetch$, type Cache } from "$lib/serv/core/cache";
import { LOCATION_PROPS, landingItemsParams$, type Location } from '$lib/serv/locations';
import { createUpgrader, open, transaction } from "$lib/util/db";
import { objectPropsEqual } from "$lib/util/object";
import { BehaviorSubject, EMPTY, Observable, combineLatestWith, distinctUntilChanged, interval, map, pairwise, repeat, share, shareReplay, switchMap, throwError } from "rxjs";

export const CacheAgeShort = 1000 * 30;
export const CacheAgeMedium = 1000 * 60 * 5;
export const CacheAgeLong = 1000 * 60 * 60 * 1;

/**
 * Cache keys (in order) to extract from URL search params.
 */
export const SPKEYS = [['latitude','lat'],['longitude','lon'],['bannerType','type']] as [string, string | string[]][];

export const DBNAME = 'groc-landing';

const subjDbInfo = new BehaviorSubject<Record<string, any> | null>(null);
// export const dbInfo$ = subjDbInfo.asObservable();
if (browser) subjDbInfo.subscribe(inf => inf && console.info(`DB ${DBNAME}:`, inf))

const upgrader = createUpgrader({
	1: (db) => [
		db.createObjectStore('cache', { keyPath: ['path','latitude','longitude','bannerType']}),
	],
});
const pdb = browser ? open(DBNAME, 1, upgrader, undefined, (i) => subjDbInfo.next(i)) : Promise.resolve(null);
export async function getDB(){
	return pdb;
}

/**
 * Clear all object stores from the database.
 * @returns Promise that resolves when all object stores have been cleared.
 */
export async function clearDB() {
	let db: IDBDatabase | null = null;
	try {
		db = await pdb;
	} catch (error) {
		console.warn('Failed to open IDB for clearing.');
	}
	if (!db) return;
	const oss = ['cache'];
	const t = transaction(db, oss, 'readwrite');
	return Promise.all(oss.map(os => t.os(os).clear())).then(() => console.log(`Cleared [${oss.join(', ')}] of IDB ${DBNAME}.`));
}

/**
 * Clear DB on logout and login.
 */
profile$.pipe(
	map(user => !!user),
	distinctUntilChanged(),
	pairwise(), // effectivley skipping the first emit
).subscribe(() => clearDB());

// ############################################################
// LOADING FUNCTIONS
// ############################################################

/**
 * Load the grocery landing items layout.
 * @param params Current location.
 * @param fetchLanding If `true` request the layout. if `false` request the stores.
 */

export function loadBase(params:GrocLandingItemsParams, fetchLanding: true):Observable<Cache<StoreLanding>>;

/**
 * Load the grocery landing items stores.
 * @param params Current location.
 * @param fetchLanding If `true` request the layout. if `false` request the stores.
 */
export function loadBase(params:GrocLandingItemsParams, fetchLanding?: false):Observable<Cache<Store[]>>;

/**
 * Load the grocery landing items layout or stores.
 * @param params Current location.
 * @param fetchLanding If `true` request the layout. if `false` request the stores.
 */
export function loadBase(params:GrocLandingItemsParams, fetchLanding = false) {
	if (fetchLanding && !('store' in params)) {
		return throwError(() => 'Groc landing params require a store.');
	}
	const msMaxCacheAge = fetchLanding ? CacheAgeMedium : CacheAgeShort; // stores contain valuable time sensitive data
	const base = fetchLanding ? `stores/${params.store}/v2/layout` : 'stores';
	if (!params.latitude || !params.longitude) return EMPTY;
	const sQuery = toLocationQueryString(params);
	const url = new URL(`ca://grocery/landing-items/${base}?${sQuery}`);
	return fetch$<StoreLanding | Store[]>(url, SPKEYS, { db: pdb, msMaxCacheAge });
}

export function obsGeoUpdates(params$:Observable<GrocLandingItemsParams>) {
	return params$.pipe(
		distinctUntilChanged((prev,cur) => 
			prev?.latitude === cur?.latitude &&
			prev?.longitude === cur?.longitude
		),
	);
}

export function obsGeoStoreUpdates(params$:Observable<GrocLandingItemsParams>) {
	return params$.pipe(
		distinctUntilChanged((prev,cur) =>
			prev?.latitude === cur?.latitude &&
			prev?.longitude === cur?.longitude &&
			prev?.store === cur?.store
		),
	);
}

export function toLocationQueryString(params:Partial<Location & Address>) {
	const arQuery = [];
	const { latitude, longitude } = params;
	if ((!latitude || !longitude ) && params.geo_location) {
		const { lat, lon } = params.geo_location;
		arQuery.push(['lat', lat.toString()], ['lon', lon.toString()]);
	} else {
		if (latitude) arQuery.push(['lat', latitude]);
		if (longitude) arQuery.push(['lon', longitude]);
	}
	const propsAddr: (keyof typeof params)[] = [
		'location_id',
		'street_number',
		'street_name',
		'suburb',
		'town',
		'postal_code',
	];
	propsAddr.forEach(prop => {
		if (typeof params[prop] === 'undefined') return;
		if (params[prop] === null) return;
		if (params[prop] === '') return;
		const val = params[prop] as string|number;
		arQuery.push([prop, encodeURIComponent(val.toString().trim())]);
	});
	return arQuery.map(([k,v]) => `${k}=${v}`).join('&');
}

const storeReloadPing$ = interval(1000 * 300).pipe(share());

/**
 * The current local stores based on the current URL location. While subscribed to this
 * stream will reload the stores every few minutes to keep the data fresh.
 */
export const stores$ = createLocationStoresStream(landingItemsParams$, storeReloadPing$).pipe(
	shareReplay(1),
)

function createLocationStoresStream(params$:Observable<GrocLandingItemsParams>, refresh$?:Observable<any>) {
	const repeatObj = refresh$ ? { delay: () => refresh$ } : { count: 0 };
	return params$.pipe(
		distinctUntilChanged(objectPropsEqual(...LOCATION_PROPS, 'store')),
		switchMap(params => loadBase(params, false).pipe(
			repeat(repeatObj),
			map(hideOvernight),
			map(cache => ({ ...cache, location: params})),
		)),
	);
}

function streamStoreParam(params$:Observable<URL|URLSearchParams>) {
	return params$.pipe(
		map(p => `searchParams` in p ? p.searchParams.get('store') : p.get('store')),
		distinctUntilChanged(),
	);
}

/**
 * Combining the store param this stream will return the selected or default store.
 * @param params$ 
 * @returns 
 */
export function streamSelectedStore(params$:Observable<URL|URLSearchParams>) {
	return streamStoreParam(params$).pipe(
		combineLatestWith(stores$),
		map(([storeId,{payload:stores}]) => {
			if (storeId) return stores?.find(store => store.id === storeId);
			return stores?.find(s => s.default);
		}),
	)
}

/**
 * This intercepts stores data and modifies overnight stores to show a different message.
 * @param cache Stores cache.
 * @returns 
 */
const hideOvernight = (cache:Cache<Store[]>) => {
	if (!cache.payload) return cache;
	cache.payload = cache.payload.map(store => {
		if (!store.open && store.landing_notifications.warning_title === 'Checkout now') {
			store.landing_notifications = {
				store_notification: '',
				warning_title: "Not accepting orders right now",
				warning_subtitle: "You can still fill up your cart and check out later."
			}
		}
		return store;
	});
	return cache;
};
